import { action, makeObservable, observable, runInAction } from 'mobx';
import {
  getStoreContext,
  parseGoalValue,
  setupMobX,
  sha256Hash,
} from '../../utils';
import { EmailAndConsents, HealthData, Password } from './userDataTypes';
import {
  UserGoalDto,
  userInfo,
  UserInfo,
  UserInfoPlusCredentials,
} from '@web/common';
import {
  requestRegistration,
  requestUpdateUserInfo,
  requestUserData,
  requestProfilePicture,
  requestUpdateProfilePicture,
  requestUpdateUserGoal,
} from './backend';
import { z } from 'zod';
import { requestMeasurementDataWithToken } from '../../modules';
import { isAxiosError } from 'axios';

export class UserDataStore {
  //these variables are only used to save data when switching between steps, making them observable leads to endless function calls when navigating back
  public healthData: HealthData | undefined = undefined;
  public emailAndConsents: EmailAndConsents | undefined = undefined;
  public password: Password | undefined = undefined;
  public nickname: string | undefined = undefined;
  public userGoal: UserGoalDto | undefined = undefined;
  public userInfo: z.input<typeof userInfo> | undefined = undefined;
  public profilePicture: ArrayBuffer | undefined = undefined;

  @observable public loadingUserData = false;
  @observable public loadedUserData = false;
  @observable public failedLoadingUserData = false;
  @observable public updatingUserInfo = false;
  @observable public updatingProfilePicture = false;
  @observable public profilePictureError = false;
  @observable public registeringUser = false;
  @observable public registrationError = false;
  @observable public storeDirty = false;
  @observable public checkingOrderStatus = false;
  @observable public measurementComplete = false;
  @observable public reportDone = false;

  constructor() {
    makeObservable(this);
  }

  public getHealthDataAndEmail() {
    const healthDataAndEmail = {
      email: this.emailAndConsents?.email,
      ...this.healthData,
    };
    return healthDataAndEmail as UserInfo;
  }

  @action public async loadUserData(kcToken: string) {
    if (!this.loadingUserData) {
      runInAction(() => {
        this.loadingUserData = true;
        this.loadedUserData = false;
      });
      try {
        const response = await requestUserData(kcToken);
        console.debug('✅ Loaded user data successfully');

        runInAction(() => {
          this.userInfo = response;
          this.userGoal = parseGoalValue(response);
          this.loadedUserData = true;
        });
        const rawPictureData = await requestProfilePicture(kcToken);
        console.debug('✅ Loaded profile picture successfully');
        runInAction(() => {
          this.profilePicture = rawPictureData;
        });
      } catch (error) {
        console.error('❌ Loading user data failed:', error);
        runInAction(() => (this.failedLoadingUserData = true));
      } finally {
        runInAction(() => (this.loadingUserData = false));
      }
    }
  }

  @action public setStoreDirty(storeDirty: boolean) {
    this.storeDirty = storeDirty;
  }

  @action public setHealthData(healthData: HealthData) {
    this.storeDirty = true;
    this.healthData = healthData;
  }

  @action public setEmailAndConsents(emailAndConsents: EmailAndConsents) {
    this.storeDirty = true;
    this.emailAndConsents = emailAndConsents;
  }

  @action public setPassword(password: Password) {
    this.storeDirty = true;
    this.password = password;
  }

  @action public async setGoal(kcToken: string, goal: UserGoalDto) {
    try {
      await requestUpdateUserGoal(kcToken, goal);
    } catch {
      console.log('Updating goal not successfull');
    } finally {
      this.userGoal = goal;
    }
  }

  @action public async registerUser() {
    if (
      this.emailAndConsents &&
      this.healthData &&
      this.password &&
      !this.registeringUser
    ) {
      this.registeringUser = true;
      this.registrationError = false;

      const paymentId = JSON.parse(
        localStorage.getItem('payment_intent') || '{}',
      );
      const client_secret = JSON.parse(
        localStorage.getItem('payment_intent_client_secret') || '{}',
      );

      const hash = await sha256Hash(client_secret);

      const userInfoPlusCredentials: UserInfoPlusCredentials = {
        ...this.healthData,
        email: this.emailAndConsents.email,
        password: this.password.password,
        marketingConsent: this.emailAndConsents.newsletterConsent || false,
      };

      try {
        await requestRegistration(userInfoPlusCredentials, paymentId, hash);
      } catch {
        runInAction(() => {
          this.registrationError = true;
        });
        console.log('Registration not successfull');
      } finally {
        runInAction(() => {
          this.registeringUser = false;
        });
      }
    }

    this.storeDirty = false;
  }

  @action public async updateUserInfo(
    kcToken: string,
    userData: z.input<typeof userInfo>,
  ) {
    if (!this.updatingUserInfo) {
      runInAction(() => {
        this.updatingUserInfo = true;
      });
      try {
        await requestUpdateUserInfo(kcToken, userData);
      } catch {
        runInAction(() => {
          this.registrationError = true;
        });
        console.log('Updating user info not successfull');
      } finally {
        await this.loadUserData(kcToken);
        runInAction(() => {
          this.updatingUserInfo = false;
        });
      }
    }
  }

  @action public async updateProfilePicture(kcToken: string, picture: Blob) {
    if (!this.updatingProfilePicture) {
      runInAction(() => {
        this.updatingProfilePicture = true;
      });
      try {
        await requestUpdateProfilePicture(kcToken, picture);
      } catch (error) {
        runInAction(() => {
          this.profilePictureError = true;
        });
        console.log('Profile picture could not be updated', error);
      } finally {
        await this.loadUserData(kcToken);
        runInAction(() => {
          this.updatingProfilePicture = false;
        });
      }
    }
  }

  @action public async checkOrderStatus(paymentToken: string) {
    if (!this.checkingOrderStatus) {
      runInAction(() => {
        this.checkingOrderStatus = true;
      });
      try {
        const response = await requestMeasurementDataWithToken(paymentToken);
        runInAction(() => {
          if (response) {
            this.reportDone = true;
          }
        });
      } catch (error: unknown) {
        if (isAxiosError(error) && error.response?.data?.errorCode === 'M003') {
          runInAction(() => {
            this.measurementComplete = true;
          });
        }
      } finally {
        runInAction(() => {
          this.checkingOrderStatus = false;
        });
      }
    }
  }
}

const userDataStore = new UserDataStore();
export const {
  Context: UserDataStoreContext,
  Provider: UserDataStoreProvider,
} = getStoreContext<UserDataStore>(userDataStore);
export default userDataStore;

const { provider, useStore } = setupMobX<UserDataStore>();
export const UserDataProvider = provider;
export const useUserDataStore = useStore;
